import {
  useDeletePostComment,
  useReportPostComment,
} from '@/actions/post-comment.actions';
import useUserProfile from '@/hooks/useUserProfile';
import { Menu, Transition } from '@headlessui/react';
import { cx } from 'cva';
import { LucideFlag } from 'lucide-react';
import React, { Fragment } from 'react';
import { HiDotsHorizontal } from 'react-icons/hi';
import { MdDeleteOutline, MdOutlineEditNote } from 'react-icons/md';
import useConfirmDialogStore from '../Dialogs/confirmDialog.store';

const confirmTitle = {
  delete: 'Delete Comment',
  report: 'Report Comment',
};

const confirmMessage = {
  delete:
    'Are you sure you want to delete this comment? This action cannot be undone.',
  report:
    'If you report this comment, an email will be sent to the admin and we may reach out to you to better understand this issue. Are you sure you want to report this comment?',
};

function PostCommentMenu(props) {
  const { comment, onEdit } = props;

  const user = useUserProfile();
  const { seekConfirmation } = useConfirmDialogStore();

  // mutations
  const { mutate: deleteComment } = useDeletePostComment();
  const { mutate: reportComment } = useReportPostComment();

  // derived states
  const isAuthor = comment?.authorId === user?.id;

  // event handlers
  const handleDelete = async () => {
    await deleteComment(comment);
  };

  const handleReport = async () => {
    await reportComment(comment);
  };

  const handleConfirm = async (action) => {
    const confirmed = await seekConfirmation(
      confirmTitle[action],
      confirmMessage[action],
      'Continue',
    );

    if (confirmed) {
      switch (action) {
        case 'delete':
          handleDelete();
          break;
        case 'report':
          handleReport();
          break;
        default:
          break;
      }
    }
  };

  // render
  return (
    <>
      <Menu as="div" className="relative inline-block text-left">
        <div>
          <Menu.Button className="rounded-full bg-transparent text-text-disabled hover:font-semibold hover:text-text-primary active:text-text-primary">
            <HiDotsHorizontal aria-hidden="true" />
          </Menu.Button>
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95">
          <Menu.Items className="absolute right-0 z-1000 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            <div className="px-1 py-1 ">
              {isAuthor && (
                <>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={onEdit}
                        className={cx(
                          'group flex w-full items-center rounded-md p-2.5 font-poppins text-14',
                          active
                            ? 'bg-primary font-semibold text-white'
                            : 'text-text-primary',
                        )}>
                        <MdOutlineEditNote
                          className="mr-2 h-5 w-5"
                          aria-hidden="true"
                        />
                        Edit
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        type="button"
                        className={cx(
                          'group flex w-full items-center rounded-md p-2.5 font-poppins text-14 font-normal',
                          active
                            ? 'bg-primary font-semibold text-white'
                            : 'text-text-primary',
                        )}
                        onClick={() => handleConfirm('delete')}>
                        <MdDeleteOutline
                          className="mr-2 h-5 w-5"
                          aria-hidden="true"
                        />
                        Delete this comment
                      </button>
                    )}
                  </Menu.Item>
                </>
              )}
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    className={cx(
                      'group flex w-full items-center rounded-md p-2.5 font-poppins text-14 font-normal',
                      active
                        ? 'bg-primary font-semibold text-white'
                        : 'text-text-primary',
                    )}
                    onClick={() => handleConfirm('report')}>
                    <LucideFlag className="mr-2 h-5 w-5" aria-hidden="true" />
                    Report this comment
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </>
  );
}

export default PostCommentMenu;
